import { Avatar, Flex, Menu, type MenuProps, Typography } from "antd";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineSetting } from "react-icons/ai";
import { MdLogout } from "react-icons/md";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router";

import { useCustomNotification } from "design-system/hooks";
import { ThreddPatternOne } from "design-system/icons";
import { pathsSchema } from "shell/platform/config/paths";
import { USER_DATA_STORAGE_PREFIX } from "shell/platform/constants/constants";
import { useClientScoping } from "shell/platform/contexts/ClientScopingContext";
import { usePMIDs } from "shell/platform/hooks/usePMIDs";
import { usePortalRoles } from "shell/platform/hooks/usePortalRoles";
import { loadAmplitude } from "shell/platform/lib/analytics/amplitudeLoader";
import { globalCognitoSignOut } from "shell/platform/lib/cognitoPasswordAuth";
import {
  clearCognitoAuthMode,
  clearLoginHint,
  clearStoredAuthProvider,
  isCognitoPasswordMode,
} from "shell/platform/lib/oidcClient";

import { ClientConfigurationModal } from "./ClientConfigurationModal";
import { PMCodeConfigurationModal } from "./PMCodeConfigurationModal";
import { RegionConfigurationModal } from "./RegionConfigurationModal";
import s from "./UserMenu.module.css";

export const UserMenu = () => {
  const { signoutRedirect, removeUser, user } = useAuth();
  const { t } = useTranslation(["common"]);
  const navigate = useNavigate();
  const { showNotification } = useCustomNotification();
  const { hasPortalReadAccess } = usePortalRoles();
  const [open, setOpen] = useState(false);
  const { availablePMIDs } = usePMIDs();
  const {
    canSwitch: canSwitchClient,
    activeSubClient,
    activeMsaClient,
  } = useClientScoping();
  const [isRegionModalOpen, setIsRegionModalOpen] = useState(false);
  const [isPMCodeModalOpen, setIsPMCodeModalOpen] = useState(false);
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { given_name, family_name, email } = user?.profile ?? {};

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setOpen(false);
    }
  };

  useEffect(() => {
    // oxlint-disable-next-line react/exhaustive-deps
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const items: MenuProps["items"] = [
    {
      key: "userInfo",
      label: (
        <div className={s.userInfo}>
          <div className={s.userName}>
            {given_name} {family_name}
          </div>
          <div className={s.userEmail}>{email}</div>
        </div>
      ),
      className: s.userInfoItem,
      disabled: true,
    },
    ...(canSwitchClient
      ? [
          {
            key: "activeClient",
            label: (
              <Flex vertical>
                <Typography.Text type="secondary">
                  {t("common:labels.client")}
                </Typography.Text>
                <Typography.Text strong>
                  {activeMsaClient?.name ?? activeMsaClient?.id ?? "—"}
                </Typography.Text>
                {activeSubClient && (
                  <>
                    <Typography.Text type="secondary">
                      {t("common:components.clientScoping.typeSubClient")}
                    </Typography.Text>
                    <Typography.Text strong>
                      {activeSubClient.name ?? activeSubClient.id}
                    </Typography.Text>
                  </>
                )}
              </Flex>
            ),
            className: s.clientInfoItem,
            disabled: true,
          },
        ]
      : []),
    ...(hasPortalReadAccess
      ? [
          {
            key: "region",
            icon: <AiOutlineSetting />,
            label: "Configure Region",
            className: s.item,
            onClick: () => setIsRegionModalOpen(true),
          },
          ...((availablePMIDs?.length ?? 0) > 0
            ? [
                {
                  key: "pmCode",
                  icon: <AiOutlineSetting />,
                  label: "Configure PM Code",
                  className: s.item,
                  onClick: () => setIsPMCodeModalOpen(true),
                },
              ]
            : []),
          ...(canSwitchClient
            ? [
                {
                  key: "client",
                  icon: <AiOutlineSetting />,
                  label: t("common:components.clientScoping.configureClient"),
                  className: s.item,
                  onClick: () => setIsClientModalOpen(true),
                },
              ]
            : []),
          {
            type: "divider" as const,
          },
        ]
      : [
          {
            type: "divider" as const,
          },
        ]),
    {
      key: "signOut",
      icon: <MdLogout />,
      label: "Logout",
      onClick: () => {
        (async () => {
          try {
            const passwordMode = isCognitoPasswordMode();
            const accessToken = user?.access_token;

            if (passwordMode && accessToken) {
              await globalCognitoSignOut(accessToken);
            }
            await removeUser();
            const amplitude = await loadAmplitude();
            amplitude?.resetAmplitude();
            if (!passwordMode) {
              await signoutRedirect();
            }

            clearStoredAuthProvider();
            clearCognitoAuthMode();
            clearLoginHint();
            for (const key of Object.keys(sessionStorage)) {
              if (key.startsWith(USER_DATA_STORAGE_PREFIX)) {
                sessionStorage.removeItem(key);
              }
            }
            if (passwordMode) {
              navigate(pathsSchema.index);
            }
          } catch (_error) {
            showNotification({
              type: "error",
              message: t("common:components.userMenu.signOutError"),
              description: t(
                "common:components.userMenu.signOutErrorDescription",
              ),
            });
          }
        })();
      },
    },
  ];

  return (
    <div
      ref={dropdownRef}
      className={clsx(s.userMenuWrapper, { [s.open]: open })}
    >
      <button
        aria-label="User Menu"
        type="button"
        className={s.userMenuButton}
        onClick={() => setOpen(o => !o)}
      >
        <Avatar shape="square" icon={<ThreddPatternOne />} />
      </button>

      <Menu
        mode="vertical"
        items={items}
        className={s.userMenuDropdown}
        selectedKeys={[]}
      />

      {isRegionModalOpen && (
        <RegionConfigurationModal
          isOpen={isRegionModalOpen}
          onClose={() => setIsRegionModalOpen(false)}
        />
      )}

      {isPMCodeModalOpen && (
        <PMCodeConfigurationModal
          isOpen={isPMCodeModalOpen}
          onClose={() => setIsPMCodeModalOpen(false)}
        />
      )}

      {isClientModalOpen && (
        <ClientConfigurationModal
          isOpen={isClientModalOpen}
          onClose={() => setIsClientModalOpen(false)}
        />
      )}
    </div>
  );
};
